import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Activity,
  ChevronRight,
  Database,
  Plus,
  Save,
  Search,
  Server,
  Shield,
  Trash2
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import {
  DELETE_POLICY_ALIAS,
  LIST_POLICY_ALIASES,
  LIST_POLICY_PRIMITIVES_DETAILED,
  SAVE_POLICY_ALIAS
} from './userAccounts/queries';
import {
  formatOrganizationalAliasLabel,
  mergeOrganizationalAliases
} from './userAccounts/organizationalAliases';

const GET_GQL_SCHEMA = gql`
  query GetGqlSchema {
    getGqlSchema
  }
`;

const GET_SECURITY_OBJECTS = gql`
  query GetSecurityObjects {
    getSecurityObjects {
      functions {
        functionName
        resourcePath
        verb
      }
    }
  }
`;

const LIST_USER_ACCOUNT_REQUEST_OPTIONS = gql`
  query SecurityManagerRequestOptions {
    roleOptions: userAccountRequestOptions(optionType: "role") {
      id
      optionType
      label
      sortOrder
      isActive
    }
    organizationOptions: userAccountRequestOptions(optionType: "organization") {
      id
      optionType
      label
      sortOrder
      isActive
    }
  }
`;

const UPDATE_SECURITY_OBJECT_PERMISSIONS = gql`
  mutation UpdateSecurityObjectPermissions(
    $type: String!
    $securityObject: String!
    $permissions: [String]
  ) {
    updateSecurityObjectPermissions(
      type: $type
      securityObject: $securityObject
      permissions: $permissions
    ) {
      message
      responseCode
    }
  }
`;

const LEGACY_ACTIONS = new Set([
  'get',
  'list',
  'search',
  'create',
  'update',
  'delete',
  'status',
  'login',
  'logout',
  'refresh',
  'lock',
  'generate',
  'generate837',
  'export',
  'import',
  'send',
  'merge',
  'enable',
  'disable',
  'reset',
  'confirm',
  'clear',
  'save',
  'start',
  'stop',
  'upload',
  'download'
]);

type AliasRecord = {
  id: number | null;
  alias: string;
  type?: string | null;
  description?: string | null;
  policies?: string[] | null;
};

type UserAccountRequestOption = {
  id: number;
  optionType: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
};

type UserAccountRequestOptionsResponse = {
  roleOptions: UserAccountRequestOption[];
  organizationOptions: UserAccountRequestOption[];
};

type AliasType = 'group' | 'policy' | 'legacy' | 'hidden' | 'organizational';

type PolicyPrimitive = {
  policy: string;
  resourcePath?: string | null;
  verb?: string | null;
};

type SchemaKind = 'query' | 'mutation' | 'subscription';

type Endpoint = {
  name: string;
  kind: SchemaKind;
  returnType?: string;
  parameters?: Record<string, unknown>;
  description?: string;
  deprecated?: boolean;
  path?: string;
  folderName?: string;
  resourcePath?: string | null;
  verb?: string | null;
  permissions?: string[] | null;
  permissionsSource?: string;
  permissionsDbState?: string;
  authentication?: any;
};

type SchemaPayload = {
  endpoints?: Endpoint[];
  types?: Record<string, any>;
};

type FunctionItem = Endpoint & {
  id: string;
  resourcePath?: string | null;
  verb?: string | null;
};

type SecurityObjectRecord = {
  functionName: string;
  resourcePath?: string | null;
  verb?: string | null;
};

const KIND_LABELS: Record<SchemaKind, string> = {
  query: 'Query',
  mutation: 'Mutation',
  subscription: 'Subscription'
};

const KIND_ICONS: Record<SchemaKind, React.ReactNode> = {
  query: <Database className="h-3.5 w-3.5 text-emerald-600" />,
  mutation: <Server className="h-3.5 w-3.5 text-amber-600" />,
  subscription: <Activity className="h-3.5 w-3.5 text-purple-600" />
};

const parseSchemaPayload = (raw?: string | null): SchemaPayload => {
  if (!raw) {
    return { endpoints: [], types: {} };
  }
  try {
    const parsed = JSON.parse(raw);
    const endpoints = Array.isArray(parsed?.endpoints) ? parsed.endpoints : [];
    const types = parsed?.types && typeof parsed.types === 'object' ? parsed.types : {};
    return { endpoints, types };
  } catch (error) {
    return { endpoints: [], types: {} };
  }
};

const normalizeTypeName = (value: string) => `${value || ''}`.replace(/[!\[\]]/g, '').trim();

const compareStrings = (left?: string | null, right?: string | null) => {
  return `${left || ''}`.localeCompare(`${right || ''}`);
};

const toTypeName = (value: unknown) => {
  if (!value) {
    return '';
  }
  if (typeof value === 'string') {
    return normalizeTypeName(value);
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (typeof record.type === 'string') {
      return normalizeTypeName(record.type);
    }
  }
  return '';
};

const formatParameters = (parameters?: Record<string, unknown>) => {
  if (!parameters) {
    return [];
  }
  return Object.entries(parameters).map(([key, value]) => ({
    name: key,
    type: typeof value === 'string' ? value : JSON.stringify(value)
  }));
};

const getPathSegments = (item: FunctionItem) => {
  const raw = `${item.path || ''}`.trim();
  if (!raw) {
    const folder = `${item.folderName || ''}`.trim();
    if (!folder) {
      return ['Uncategorized'];
    }
    return [folder];
  }
  const segments = raw.split('/').filter(Boolean);
  if (segments.length === 0) {
    return ['Uncategorized'];
  }
  const last = segments[segments.length - 1];
  if (last === 'schema') {
    segments.pop();
  }
  if (segments.length === 0) {
    return ['Uncategorized'];
  }
  return segments;
};

const toDomId = (prefix: string, value: string) => {
  const normalized = `${value || ''}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${prefix}-${normalized || 'item'}`;
};

const normalizePermissions = (value?: string[] | null) => {
  if (!Array.isArray(value)) {
    return [];
  }
  const seen = new Set<string>();
  value.forEach((permission) => {
    const normalized = normalizePolicyString(permission || '');
    if (!normalized) {
      return;
    }
    seen.add(normalized);
  });
  return Array.from(seen);
};

type ParsedPolicy = {
  root: string;
  identity: string[];
  action?: string;
};

const normalizeLegacyPolicy = (value: string) => {
  if (!value || value.includes('~')) {
    return value;
  }
  const segments = value.split(':');
  if (segments.length !== 3) {
    return value;
  }
  if (segments.some((segment) => segment.length === 0)) {
    return value;
  }
  if (!LEGACY_ACTIONS.has(segments[2])) {
    return value;
  }
  return `${segments[0]}:${segments[1]}~${segments[2]}`;
};

const normalizeWildcardSegment = (value: string) => {
  if (!value) {
    return '';
  }
  if (/^\*+$/.test(value)) {
    return '*';
  }
  return value;
};

const parsePolicy = (value: string): ParsedPolicy => {
  const raw = `${value || ''}`.trim();
  if (!raw) {
    return { root: '', identity: [] };
  }
  const parts = raw.split('~');
  if (parts.length > 2) {
    return { root: '', identity: [] };
  }
  const identityPart = parts[0].trim();
  if (!identityPart) {
    return { root: '', identity: [] };
  }
  const identity = identityPart.split(':');
  if (identity.length === 0 || identity.some((segment) => segment.length === 0)) {
    return { root: '', identity: [] };
  }
  const root = identity[0];
  if (!root) {
    return { root: '', identity: [] };
  }
  if (parts.length === 1) {
    return { root, identity };
  }
  const action = parts[1].trim();
  if (!action) {
    return { root: '', identity: [] };
  }
  return { root, identity, action };
};

const normalizePolicyString = (value: string) => {
  const raw = `${value || ''}`.trim();
  if (!raw) {
    return '';
  }
  const migrated = normalizeLegacyPolicy(raw);
  const parsed = parsePolicy(migrated);
  if (parsed.identity.length === 0 || !parsed.action) {
    return '';
  }
  const identity = parsed.identity.map((segment) => normalizeWildcardSegment(segment));
  const action = normalizeWildcardSegment(parsed.action || '');
  if (!action || identity.some((segment) => !segment)) {
    return '';
  }
  return `${identity.join(':')}~${action}`;
};

const splitPolicy = (policy: string) => {
  const normalized = normalizePolicyString(policy);
  const parsed = parsePolicy(normalized);
  return {
    root: parsed.root,
    resource: parsed.identity.length > 1 ? parsed.identity[1] : '',
    field: parsed.identity.length > 2 ? parsed.identity[2] : '',
    action: parsed.action || ''
  };
};

const hasInvalidPermissionSegments = (permissions?: string[] | null) => {
  if (!Array.isArray(permissions) || permissions.length === 0) {
    return true;
  }
  return permissions.some((permission) => {
    const parsed = parsePolicy(normalizePolicyString(permission || ''));
    if (!parsed.root || !parsed.action) {
      return true;
    }
    if (parsed.root === 'type') {
      return parsed.identity.length !== 3;
    }
    return parsed.identity.length !== 2;
  });
};

const hasMissingAuthPolicy = (permissions?: string[] | null) => {
  const normalized = normalizePermissions(permissions);
  return normalized.includes('system:auth~missing');
};

const buildPolicyString = (segments: string[]) => {
  const action = `${segments[0] || ''}`.trim();
  const root = `${segments[1] || ''}`.trim();
  const resource = `${segments[2] || ''}`.trim();
  const field = `${segments[3] || ''}`.trim();
  if (!action || !root || !resource) {
    return '';
  }
  if (root === 'type') {
    if (!field) {
      return '';
    }
    return normalizePolicyString(`${root}:${resource}:${field}~${action}`);
  }
  return normalizePolicyString(`${root}:${resource}~${action}`);
};

const isUnauthenticatedEndpoint = (entry: Endpoint) => {
  if (!entry) {
    return false;
  }
  if ((entry as any).authentication?.unauthenticated === true) {
    return true;
  }
  return entry.permissionsSource === 'none' || (!entry.permissionsSource && !entry.permissionsDbState);
};

const isMailEndpoint = (entry: Endpoint) => {
  if (!entry) {
    return false;
  }
  const path = `${(entry as any).resourcePath || (entry as any).path || ''}`;
  return path.startsWith('messages');
};

const uniquePermissions = (permissions: string[]) => {
  return Array.from(new Set(permissions));
};

const isOrganizationalAliasName = (value: unknown) => {
  const parts = `${value || ''}`
    .split('|')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return parts.length === 2;
};

const normalizeAliasType = (value: unknown): AliasType | '' => {
  const normalized = `${value || ''}`.trim().toLowerCase();
  if (
    normalized === 'group' ||
    normalized === 'policy' ||
    normalized === 'legacy' ||
    normalized === 'hidden' ||
    normalized === 'organizational'
  ) {
    return normalized;
  }
  return '';
};

const resolveAliasType = (alias: AliasRecord): AliasType => {
  const explicitType = normalizeAliasType(alias?.type);
  if (explicitType) {
    return explicitType;
  }
  const normalizedAlias = `${alias?.alias || ''}`.trim().toLowerCase();
  if (normalizedAlias.startsWith('legacy:')) {
    return 'legacy';
  }
  if (normalizedAlias.startsWith('hidden:')) {
    return 'hidden';
  }
  if (isOrganizationalAliasName(alias?.alias)) {
    return 'organizational';
  }
  return 'group';
};

const aliasTypeLabel: Record<AliasType, string> = {
  group: 'Group',
  policy: 'Feature',
  legacy: 'Legacy',
  hidden: 'Hidden',
  organizational: 'Organizational'
};

const aliasTypeBadgeClass: Record<AliasType, string> = {
  group: 'border border-blue-200 bg-blue-100 text-blue-800',
  policy: 'border border-emerald-200 bg-emerald-100 text-emerald-800',
  legacy: 'border border-amber-200 bg-amber-100 text-amber-800',
  hidden: 'border border-slate-200 bg-slate-100 text-slate-700',
  organizational: 'border border-violet-200 bg-violet-100 text-violet-800'
};

const getDraftAliasType = (aliasName: string, aliasType: unknown): AliasType => {
  if (isOrganizationalAliasName(aliasName)) {
    return 'organizational';
  }
  return normalizeAliasType(aliasType) || 'group';
};

const getAliasDisplayLabel = (alias: AliasRecord) => {
  if (resolveAliasType(alias) !== 'organizational') {
    return alias.alias;
  }
  return formatOrganizationalAliasLabel(alias.alias);
};

const toIdSegment = (value: string) => {
  const normalized = `${value || ''}`
    .toLowerCase()
    .replace(/\*/g, 'all')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (!normalized) {
    return 'item';
  }
  return normalized;
};

const buildId = (prefix: string, value: string) => `${prefix}-${toIdSegment(value)}`;

const sortPolicySegmentList = (values: Set<string>) => {
  const list = Array.from(values);
  return list.sort((a, b) => compareStrings(a, b));
};

export const SecurityManager = () => {
  const {
    data: aliasData,
    loading: aliasLoading,
    error: aliasError,
    refetch: refetchAliases
  } = useQuery<{ listPolicyAliases: AliasRecord[] }>(LIST_POLICY_ALIASES);
  const { data: optionData } = useQuery<UserAccountRequestOptionsResponse>(LIST_USER_ACCOUNT_REQUEST_OPTIONS);
  const {
    data: securityObjectData,
    loading: securityObjectLoading,
    error: securityObjectError
  } = useQuery<{ getSecurityObjects: { functions: SecurityObjectRecord[] } }>(GET_SECURITY_OBJECTS);
  const {
    data: schemaData,
    loading: schemaLoading,
    error: schemaError,
    refetch: refetchSchema
  } = useQuery<{ getGqlSchema: string }>(GET_GQL_SCHEMA);
  const { data: primitiveData } = useQuery<{
    listPolicyPrimitivesDetailed: PolicyPrimitive[];
  }>(LIST_POLICY_PRIMITIVES_DETAILED);
  const [saveAlias, { loading: savingAlias }] = useMutation(SAVE_POLICY_ALIAS);
  const [deleteAlias, { loading: deletingAlias }] = useMutation(DELETE_POLICY_ALIAS);
  const [updateSecurityObjectPermissions, { loading: savingFunctionPermissions }] = useMutation(
    UPDATE_SECURITY_OBJECT_PERMISSIONS
  );

  const [activeTab, setActiveTab] = useState<'summary' | 'groups' | 'functions' | 'policies'>(
    'summary'
  );
  const [selectedAlias, setSelectedAlias] = useState<string | null>(null);
  const [isNewGroup, setIsNewGroup] = useState(false);
  const [isListView, setIsListView] = useState(true);
  const [aliasDraft, setAliasDraft] = useState<AliasRecord>({
    id: 0,
    alias: '',
    type: 'group',
    description: '',
    policies: []
  });
  const [groupSearch, setGroupSearch] = useState('');
  const [policyBuilder, setPolicyBuilder] = useState({
    namespace: '*',
    access: '*',
    element: '*',
    field: '*',
    scope: '*'
  });
  const [expandedNamespaces, setExpandedNamespaces] = useState<string[]>([]);
  const [expandedElements, setExpandedElements] = useState<Record<string, string[]>>({});
  const [focusedPolicy, setFocusedPolicy] = useState<string | null>(null);
  const [functionSearch, setFunctionSearch] = useState('');
  const [selectedKind, setSelectedKind] = useState<SchemaKind | null>(null);
  const [selectedFunctionId, setSelectedFunctionId] = useState<string | null>(null);
  const [policyLibrarySearch, setPolicyLibrarySearch] = useState('');
  const [functionPolicyBuilder, setFunctionPolicyBuilder] = useState({
    namespace: '*',
    access: '*',
    element: '*',
    field: '*',
    scope: '*'
  });
  const [functionExpandedNamespaces, setFunctionExpandedNamespaces] = useState<string[]>([]);
  const [functionExpandedElements, setFunctionExpandedElements] = useState<Record<string, string[]>>({});
  const [functionFocusedPolicy, setFunctionFocusedPolicy] = useState<string | null>(null);
  const [functionDraftPermissions, setFunctionDraftPermissions] = useState<string[]>([]);

  const schemaPayload = useMemo(
    () => parseSchemaPayload(schemaData?.getGqlSchema),
    [schemaData?.getGqlSchema]
  );

  const securityObjectMap = useMemo(() => {
    const map = new Map<string, SecurityObjectRecord>();
    const list = securityObjectData?.getSecurityObjects?.functions || [];
    list.forEach((fn) => {
      if (!fn || !fn.functionName) {
        return;
      }
      map.set(fn.functionName, fn);
    });
    return map;
  }, [securityObjectData?.getSecurityObjects?.functions]);

  const functionItems = useMemo(() => {
    const endpoints = schemaPayload.endpoints || [];
    return endpoints.map((entry) => ({
      ...entry,
      id: `${entry.kind}:${entry.name}`,
      ...(securityObjectMap.get(entry.name) || {})
    }));
  }, [schemaPayload.endpoints, securityObjectMap]);

  const itemsById = useMemo(() => new Map(functionItems.map((item) => [item.id, item])), [
    functionItems
  ]);

  const filteredBySearch = useMemo(() => {
    const trimmed = functionSearch.trim().toLowerCase();
    if (!trimmed) {
      return functionItems;
    }
    return functionItems.filter((item) => item.name.toLowerCase().includes(trimmed));
  }, [functionItems, functionSearch]);

  const filteredByKind = useMemo(() => {
    if (!selectedKind) {
      return filteredBySearch;
    }
    return filteredBySearch.filter((item) => item.kind === selectedKind);
  }, [filteredBySearch, selectedKind]);

  const selectedFunction = selectedFunctionId ? itemsById.get(selectedFunctionId) : undefined;
  const selectedIsUnauth = selectedFunction ? isUnauthenticatedEndpoint(selectedFunction) : false;
  const selectedIsMail = selectedFunction ? isMailEndpoint(selectedFunction) : false;

  const kindCounts = useMemo(() => {
    const counts = { query: 0, mutation: 0, subscription: 0 } as Record<SchemaKind, number>;
    filteredBySearch.forEach((item) => {
      counts[item.kind] += 1;
    });
    return counts;
  }, [filteredBySearch]);

  const summaryFunctionItems = useMemo(() => {
    return [...functionItems].sort((a, b) => compareStrings(a.name, b.name));
  }, [functionItems]);

  const functionList = useMemo(() => {
    return [...filteredByKind].sort((a, b) => compareStrings(a.name, b.name));
  }, [filteredByKind]);

  const errorEndpoints = useMemo(() => {
    const list: Array<{
      id: string;
      name: string;
      kind: SchemaKind;
      reasons: string[];
      permissions?: string[] | null;
    }> = [];
    (schemaPayload.endpoints || []).forEach((entry) => {
      if (isUnauthenticatedEndpoint(entry) || isMailEndpoint(entry)) {
        return;
      }
      const reasons: string[] = [];
      if (entry.permissionsDbState === 'missing') {
        reasons.push('Missing security entry');
      }
      if (hasMissingAuthPolicy(entry.permissions)) {
        reasons.push('Missing permissions definition');
      }
      if (
        Array.isArray(entry.permissions) &&
        entry.permissions.length === 1 &&
        entry.permissions[0] === 'system:missing~*'
      ) {
        reasons.push('Default superuser-only policy (system:missing~*)');
      }
      if (hasInvalidPermissionSegments(entry.permissions)) {
        reasons.push('Permissions must match <namespace>:<resource>~<action> or type:Type:field~<action>');
      }
      if (reasons.length === 0) {
        return;
      }
      list.push({
        id: `${entry.kind}:${entry.name}`,
        name: entry.name,
        kind: entry.kind,
        reasons,
        permissions: entry.permissions
      });
    });
  return list.sort((a, b) => compareStrings(a.name, b.name));
  }, [schemaPayload.endpoints]);

  const missingEntryCount = useMemo(() => {
    return errorEndpoints.filter((entry) => entry.reasons.includes('Missing security entry')).length;
  }, [errorEndpoints]);

  const invalidPermissionCount = useMemo(() => {
    return errorEndpoints.filter((entry) =>
      entry.reasons.includes('Permissions must match <namespace>:<resource>~<action> or type:Type:field~<action>')
    ).length;
  }, [errorEndpoints]);

  const unauthenticatedEndpoints = useMemo(() => {
    return (schemaPayload.endpoints || []).filter((entry) => isUnauthenticatedEndpoint(entry));
  }, [schemaPayload.endpoints]);

  const mailEndpoints = useMemo(() => {
    return (schemaPayload.endpoints || []).filter((entry) => isMailEndpoint(entry));
  }, [schemaPayload.endpoints]);

  const errorIdSet = useMemo(() => {
    return new Set(errorEndpoints.map((entry) => entry.id));
  }, [errorEndpoints]);

  const selectedFunctionErrors = useMemo(() => {
    if (!selectedFunction) {
      return null;
    }
    return errorEndpoints.find((entry) => entry.id === selectedFunction.id) || null;
  }, [errorEndpoints, selectedFunction]);

  const organizationOptions = useMemo(() => {
    const rows = Array.isArray(optionData?.organizationOptions) ? optionData?.organizationOptions || [] : [];
    return rows
      .filter((row) => row?.isActive !== false && `${row?.label || ''}`.trim().length > 0)
      .sort((left, right) => {
        const sortDelta = Number(left?.sortOrder || 0) - Number(right?.sortOrder || 0);
        if (sortDelta !== 0) {
          return sortDelta;
        }
        return `${left?.label || ''}`.localeCompare(`${right?.label || ''}`);
      })
      .map((row) => `${row?.label || ''}`.trim());
  }, [optionData?.organizationOptions]);

  const roleOptions = useMemo(() => {
    const rows = Array.isArray(optionData?.roleOptions) ? optionData?.roleOptions || [] : [];
    return rows
      .filter((row) => row?.isActive !== false && `${row?.label || ''}`.trim().length > 0)
      .sort((left, right) => {
        const sortDelta = Number(left?.sortOrder || 0) - Number(right?.sortOrder || 0);
        if (sortDelta !== 0) {
          return sortDelta;
        }
        return `${left?.label || ''}`.localeCompare(`${right?.label || ''}`);
      })
      .map((row) => `${row?.label || ''}`.trim());
  }, [optionData?.roleOptions]);

  const aliasList = useMemo(() => {
    const sorted = mergeOrganizationalAliases(aliasData?.listPolicyAliases || [], organizationOptions, roleOptions).map((alias) => ({
      ...alias,
      id: alias.id ?? null
    })) as AliasRecord[];
    const term = groupSearch.trim().toLowerCase();
    if (!term) {
      return sorted;
    }
    return sorted.filter((alias) => {
      if (alias.alias.toLowerCase().includes(term)) {
        return true;
      }
      return getAliasDisplayLabel(alias).toLowerCase().includes(term);
    });
  }, [aliasData?.listPolicyAliases, groupSearch, organizationOptions, roleOptions]);

  useEffect(() => {
    if (aliasList.length === 0) {
      return;
    }
    if (selectedAlias) {
      return;
    }
    if (isNewGroup) {
      return;
    }
    setSelectedAlias(aliasList[0].alias);
  }, [aliasList, selectedAlias, isNewGroup]);

  useEffect(() => {
    if (!selectedAlias) {
      return;
    }
    const match = aliasList.find((alias) => alias.alias === selectedAlias);
    if (!match) {
      return;
    }
    if (isNewGroup) {
      return;
    }
    setAliasDraft({
      id: match.id,
      alias: match.alias,
      type: resolveAliasType(match),
      description: match.description || '',
      policies: normalizePermissions(match.policies)
    });
  }, [aliasList, selectedAlias, isNewGroup]);

  useEffect(() => {
    if (!selectedFunction) {
      setFunctionDraftPermissions([]);
      return;
    }
    setFunctionDraftPermissions(normalizePermissions(selectedFunction.permissions));
    setFunctionFocusedPolicy(null);
    setFunctionExpandedNamespaces([]);
    setFunctionExpandedElements({});
    const parts = `${selectedFunction.resourcePath || ''}`.split(':').filter(Boolean);
    const ns = parts.length > 0 ? parts[0] : '*';
    const resource = parts.length > 1 ? parts.slice(1).join(':') : '*';
    const verb = `${selectedFunction.verb || ''}`.trim() || functionPolicyBuilder.access;
    setFunctionPolicyBuilder((prev) => ({
      ...prev,
      namespace: ns || '*',
      element: resource || '*',
      field: prev.field,
      access: verb
    }));
  }, [selectedFunctionId, selectedFunction?.permissions, selectedFunction?.resourcePath, selectedFunction?.verb, functionPolicyBuilder.access]);

  const selectedPermissions = useMemo(
    () => normalizePermissions(aliasDraft.policies),
    [aliasDraft.policies]
  );

  const permissionsSet = useMemo(
    () => new Set(selectedPermissions),
    [selectedPermissions]
  );

  const assignedPolicies = useMemo(() => {
    return [...selectedPermissions].sort((a, b) => compareStrings(a, b));
  }, [selectedPermissions]);

  const functionAssignedPermissions = useMemo(
    () => normalizePermissions(functionDraftPermissions),
    [functionDraftPermissions]
  );

  const functionPermissionsSet = useMemo(
    () => new Set(functionAssignedPermissions),
    [functionAssignedPermissions]
  );

  const functionAssignedPolicies = useMemo(() => {
    return [...functionAssignedPermissions].sort((a, b) => compareStrings(a, b));
  }, [functionAssignedPermissions]);

  const schemaPolicies = useMemo(() => {
    const policies = new Set<string>();
    (schemaPayload.endpoints || []).forEach((entry) => {
      if (entry.permissionsSource !== 'db') {
        return;
      }
      normalizePermissions(entry.permissions).forEach((permission) => {
        policies.add(permission);
      });
    });
    return Array.from(policies);
  }, [schemaPayload.endpoints]);

  const primitivePolicies = useMemo(() => {
    const list = primitiveData?.listPolicyPrimitivesDetailed || [];
    return list.map((entry) => `${entry.policy || ''}`.trim()).filter((p) => p.length > 0);
  }, [primitiveData?.listPolicyPrimitivesDetailed]);

  const knownPolicies = useMemo(() => {
    const policies = new Set<string>();
    normalizePermissions(primitivePolicies).forEach((permission) => {
      policies.add(permission);
    });
    schemaPolicies.forEach((permission) => {
      policies.add(permission);
    });
    selectedPermissions.forEach((permission) => {
      policies.add(permission);
    });
    return Array.from(policies).sort((a, b) => compareStrings(a, b));
  }, [primitivePolicies, schemaPolicies, selectedPermissions]);

  const policyLibrary = useMemo(() => {
    const policies = new Set<string>();
    normalizePermissions(primitivePolicies).forEach((permission) => {
      policies.add(permission);
    });
    schemaPolicies.forEach((permission) => {
      policies.add(permission);
    });
    return Array.from(policies).sort((a, b) => compareStrings(a, b));
  }, [primitivePolicies, schemaPolicies]);

  const filteredPolicyLibrary = useMemo(() => {
    const trimmed = policyLibrarySearch.trim().toLowerCase();
    if (!trimmed) {
      return policyLibrary;
    }
    return policyLibrary.filter((policy) => policy.toLowerCase().includes(trimmed));
  }, [policyLibrary, policyLibrarySearch]);

  const policyTree = useMemo(() => {
    const allowedNamespaces = new Set([
      '*',
      'program',
      'admin',
      'system',
      'tools',
      'billing',
      'messages',
      'reports',
      'type'
    ]);
    const allowedActions = new Set(['*', 'read', 'write', 'full', 'exec', 'create', 'delete', 'access']);
    const domainMap = new Map<string, Map<string, Set<string>>>();
    const typeMap = new Map<string, Map<string, Set<string>>>();

    knownPolicies.forEach((policy) => {
      const parsed = parsePolicy(normalizePolicyString(policy));
      if (!parsed.root || !parsed.action) {
        return;
      }
      if (!allowedNamespaces.has(parsed.root)) {
        return;
      }
      if (!allowedActions.has(parsed.action)) {
        return;
      }
      if (parsed.root === 'type') {
        if (parsed.identity.length !== 3) {
          return;
        }
        const typeName = parsed.identity[1];
        const field = parsed.identity[2];
        const fields = typeMap.get(typeName) || new Map<string, Set<string>>();
        const actions = fields.get(field) || new Set<string>();
        if (allowedActions.has(parsed.action)) {
          actions.add(parsed.action);
        }
        fields.set(field, actions);
        typeMap.set(typeName, fields);
        return;
      }
      if (parsed.identity.length !== 2) {
        return;
      }
      const resource = parsed.identity[1];
      const resources = domainMap.get(parsed.root) || new Map<string, Set<string>>();
      const actions = resources.get(resource) || new Set<string>();
      actions.add(parsed.action);
      resources.set(resource, actions);
      domainMap.set(parsed.root, resources);
    });

    const entries = Array.from(domainMap.entries()).map(([root, resources]) => ({
      root,
      resources: Array.from(resources.entries())
        .map(([resource, actions]) => ({
          resource,
          actions: Array.from(actions)
            .filter((a) => allowedActions.has(a))
            .sort((a, b) => compareStrings(a, b)),
          fields: [] as Array<{ field: string; actions: string[] }>
        }))
        .sort((a, b) => compareStrings(a.resource, b.resource))
    }));

    if (typeMap.size > 0) {
      entries.push({
        root: 'type',
        resources: Array.from(typeMap.entries())
          .map(([typeName, fields]) => ({
            resource: typeName,
            actions: [] as string[],
            fields: Array.from(fields.entries())
              .map(([field, actions]) => ({
                field,
                actions: Array.from(actions).sort((a, b) => compareStrings(a, b))
              }))
              .sort((a, b) => compareStrings(a.field, b.field))
          }))
          .sort((a, b) => compareStrings(a.resource, b.resource))
      });
    }

    return entries.sort((a, b) => compareStrings(a.root, b.root));
  }, [knownPolicies]);

const policySegments = useMemo(() => {
    const allowedNamespaces = new Set(['program', 'admin', 'system', 'tools', 'billing', 'messages', 'reports', 'type']);
    const namespaces = new Set<string>(allowedNamespaces);
    (primitiveData?.listPolicyPrimitivesDetailed || []).forEach((entry) => {
      const root = `${entry.resourcePath || ''}`.split(':')[0]?.trim();
      if (root && allowedNamespaces.has(root)) {
        namespaces.add(root);
      }
    });
    securityObjectMap.forEach((fn) => {
      const root = `${fn.resourcePath || ''}`.split(':')[0]?.trim();
      if (root && allowedNamespaces.has(root)) {
        namespaces.add(root);
      }
    });

    const allowedActions = new Set(['read', 'write', 'exec', 'create', 'delete', 'access']);
    const actions = new Set<string>(allowedActions);
    (primitiveData?.listPolicyPrimitivesDetailed || []).forEach((entry) => {
      const verb = `${entry.verb || ''}`.trim();
      if (verb && allowedActions.has(verb)) {
        actions.add(verb);
      }
    });
    securityObjectMap.forEach((fn) => {
      const verb = `${fn.verb || ''}`.trim();
      if (verb && allowedActions.has(verb)) {
        actions.add(verb);
      }
    });
    const elements = new Set<string>();
    const fields = new Set<string>();

    policyTree.forEach((entry) => {
      if (allowedNamespaces.has(entry.root)) {
        namespaces.add(entry.root);
      }
      entry.resources.forEach((resource) => {
        elements.add(resource.resource);
        resource.actions.forEach((action) => {
          if (allowedActions.has(action)) {
            actions.add(action);
          }
        });
        resource.fields.forEach((field) => {
          fields.add(field.field);
          field.actions.forEach((action) => {
            if (allowedActions.has(action)) {
              actions.add(action);
            }
          });
        });
      });
    });

    return {
      namespaces: sortPolicySegmentList(namespaces),
      access: sortPolicySegmentList(actions),
      elements: sortPolicySegmentList(elements),
      fields: sortPolicySegmentList(fields)
    };
  }, [policyTree]);

  const typeFieldOptions = useMemo(() => {
    const map = new Map<string, string[]>();
    const types = schemaPayload.types || {};
    Object.entries(types).forEach(([typeName, entry]) => {
      if (!entry || typeof entry !== 'object') {
        return;
      }
      const fields = {
        ...(entry as any).fields,
        ...(entry as any).foreignFields
      };
      if (!fields || typeof fields !== 'object') {
        return;
      }
      const fieldList = Object.keys(fields).sort((a, b) => a.localeCompare(b));
      map.set(typeName, fieldList);
    });
    return map;
  }, [schemaPayload.types]);

  const elementOptionsByNamespace = useMemo(() => {
    const map = new Map<string, string[]>();
    policyTree.forEach((entry) => {
      map.set(
        entry.root,
        entry.resources.map((item) => item.resource)
      );
    });
    if (typeFieldOptions.size > 0) {
      map.set('type', Array.from(typeFieldOptions.keys()).sort((a, b) => compareStrings(a, b)));
    }
    securityObjectMap.forEach((fn) => {
      const parts = `${fn.resourcePath || ''}`.split(':').filter(Boolean);
      if (parts.length === 0) {
        return;
      }
      const [root, ...rest] = parts;
      if (!root) {
        return;
      }
      const resource = rest.join(':') || fn.functionName;
      const current = map.get(root) || [];
      if (!current.includes(resource)) {
        map.set(root, [...current, resource].sort((a, b) => compareStrings(a, b)));
      }
    });
    return map;
  }, [policyTree, typeFieldOptions, securityObjectMap]);

  const fieldOptionsByNamespaceElement = useMemo(() => {
    const map = new Map<string, Map<string, string[]>>();
    const elementMap = new Map<string, string[]>();
    typeFieldOptions.forEach((fields, typeName) => {
      elementMap.set(typeName, fields);
    });
    if (elementMap.size > 0) {
      map.set('type', elementMap);
    }
    return map;
  }, [typeFieldOptions]);

  useEffect(() => {
    const next = { ...policyBuilder };
    let changed = false;

    if (!policySegments.namespaces.includes(next.namespace)) {
      next.namespace = policySegments.namespaces[0] || '*';
      changed = true;
    }
    if (!policySegments.access.includes(next.access)) {
      next.access = policySegments.access[0] || '*';
      changed = true;
    }

    if (!changed) {
      return;
    }

    setPolicyBuilder(next);
  }, [policySegments.namespaces, policySegments.access]);

  useEffect(() => {
    const next = { ...functionPolicyBuilder };
    let changed = false;

    if (!policySegments.namespaces.includes(next.namespace)) {
      next.namespace = policySegments.namespaces[0] || '*';
      changed = true;
    }
    if (!policySegments.access.includes(next.access)) {
      next.access = policySegments.access[0] || '*';
      changed = true;
    }

    if (!changed) {
      return;
    }

    setFunctionPolicyBuilder(next);
  }, [policySegments.namespaces, policySegments.access]);

  const availableElements = useMemo(() => {
    if (policyBuilder.namespace === '*') {
      return ['*'];
    }
    const list = elementOptionsByNamespace.get(policyBuilder.namespace) || [];
    return ['*', ...list];
  }, [policyBuilder.namespace, elementOptionsByNamespace]);

  const availableFields = useMemo(() => {
    if (policyBuilder.namespace !== 'type') {
      return ['*'];
    }
    if (policyBuilder.element === '*') {
      return ['*'];
    }
    const elementMap = fieldOptionsByNamespaceElement.get(policyBuilder.namespace);
    if (!elementMap) {
      return ['*'];
    }
    const fields = elementMap.get(policyBuilder.element) || [];
    if (fields.length === 0) {
      return ['*'];
    }
    return ['*', ...fields];
  }, [policyBuilder.namespace, policyBuilder.element, fieldOptionsByNamespaceElement]);

  const functionAvailableElements = useMemo(() => {
    if (functionPolicyBuilder.namespace === '*') {
      return ['*'];
    }
    const list = elementOptionsByNamespace.get(functionPolicyBuilder.namespace) || [];
    return ['*', ...list];
  }, [functionPolicyBuilder.namespace, elementOptionsByNamespace]);

  const functionAvailableFields = useMemo(() => {
    if (functionPolicyBuilder.namespace !== 'type') {
      return ['*'];
    }
    if (functionPolicyBuilder.element === '*') {
      return ['*'];
    }
    const elementMap = fieldOptionsByNamespaceElement.get(functionPolicyBuilder.namespace);
    if (!elementMap) {
      return ['*'];
    }
    const fields = elementMap.get(functionPolicyBuilder.element) || [];
    if (fields.length === 0) {
      return ['*'];
    }
    return ['*', ...fields];
  }, [
    functionPolicyBuilder.namespace,
    functionPolicyBuilder.element,
    fieldOptionsByNamespaceElement
  ]);

  useEffect(() => {
    if (!availableElements.includes(policyBuilder.element)) {
      setPolicyBuilder((prev) => ({ ...prev, element: '*' }));
    }
  }, [availableElements, policyBuilder.element]);

  useEffect(() => {
    if (!availableFields.includes(policyBuilder.field)) {
      setPolicyBuilder((prev) => ({ ...prev, field: '*' }));
    }
  }, [availableFields, policyBuilder.field]);

  useEffect(() => {
    if (!functionAvailableElements.includes(functionPolicyBuilder.element)) {
      setFunctionPolicyBuilder((prev) => ({ ...prev, element: '*' }));
    }
  }, [functionAvailableElements, functionPolicyBuilder.element]);

  useEffect(() => {
    if (!functionAvailableFields.includes(functionPolicyBuilder.field)) {
      setFunctionPolicyBuilder((prev) => ({ ...prev, field: '*' }));
    }
  }, [functionAvailableFields, functionPolicyBuilder.field]);

  const namespaceOptions = useMemo(() => {
    if (policySegments.namespaces.length === 0) {
      return ['*'];
    }
    return ['*', ...policySegments.namespaces];
  }, [policySegments.namespaces]);

  useEffect(() => {
    setFocusedPolicy(null);
    setExpandedNamespaces([]);
    setExpandedElements({});
  }, [selectedAlias]);

  const applyPermissions = async (nextPermissions: string[]) => {
    const aliasName = aliasDraft.alias.trim();
    if (!aliasName) {
      toast.error('Group name is required');
      return;
    }
    const payload = {
      alias: aliasName,
      type: getDraftAliasType(aliasName, aliasDraft.type),
      description: aliasDraft.description || '',
      policies: uniquePermissions(nextPermissions)
    };
    try {
      await saveAlias({ variables: { alias: payload } });
      setAliasDraft((prev) => ({
        ...prev,
        alias: payload.alias,
        type: payload.type,
        description: payload.description,
        policies: payload.policies
      }));
      setIsNewGroup(false);
      await refetchAliases();
      setSelectedAlias(payload.alias);
    } catch (error) {
      toast.error('Failed to save group policies');
    }
  };

  const handleTogglePolicy = async (policy: string) => {
    const trimmed = `${policy || ''}`.trim();
    if (!trimmed) {
      return;
    }
    const isAssigned = permissionsSet.has(trimmed);
    const next = new Set(selectedPermissions);
    if (isAssigned) {
      next.delete(trimmed);
    }
    if (!isAssigned) {
      next.add(trimmed);
    }
    await applyPermissions(Array.from(next));
  };

  const handleAddPolicy = async () => {
    const policy = buildPolicyString([
      policyBuilder.access,
      policyBuilder.namespace,
      policyBuilder.element,
      policyBuilder.field,
      policyBuilder.scope
    ]);
    if (!policy) {
      toast.error('Policy is required');
      return;
    }
    if (permissionsSet.has(policy)) {
      toast.error('Policy already assigned');
      return;
    }
    const next = new Set(selectedPermissions);
    next.add(policy);
    await applyPermissions(Array.from(next));
  };

  const handleAddKnownPolicy = async (policy: string) => {
    const normalized = normalizePolicyString(policy || '');
    if (!normalized) {
      return;
    }
    if (permissionsSet.has(normalized)) {
      return;
    }
    const next = new Set(selectedPermissions);
    next.add(normalized);
    await applyPermissions(Array.from(next));
  };

  const handleAddElementPolicy = async (root: string, resource: string, action: string) => {
    const policy = buildPolicyString([action, root, resource, '*', '*']);
    if (!policy) {
      return;
    }
    await handleAddKnownPolicy(policy);
  };

  const handleAddFieldPolicy = async (
    root: string,
    resource: string,
    field: string,
    action: string
  ) => {
    const policy = buildPolicyString([action, root, resource, field, '*']);
    if (!policy) {
      return;
    }
    await handleAddKnownPolicy(policy);
  };

  const focusOnPolicy = (policy: string) => {
    const { root, resource, field } = splitPolicy(policy);
    if (root && root !== '*') {
      setExpandedNamespaces((prev) => (prev.includes(root) ? prev : [...prev, root]));
    }
    if (root && root !== '*' && resource && resource !== '*') {
      setExpandedElements((prev) => {
        const current = prev[root] || [];
        if (current.includes(resource)) {
          return prev;
        }
        return { ...prev, [root]: [...current, resource] };
      });
    }
    setFocusedPolicy(policy);
    if (!root || root === '*') {
      setExpandedNamespaces([]);
      setExpandedElements({});
      return;
    }
    if (!resource || resource === '*') {
      return;
    }
    if (!field || field === '*') {
      return;
    }
  };

  const toggleNamespace = (namespace: string) => {
    setExpandedNamespaces((prev) => {
      if (prev.includes(namespace)) {
        return prev.filter((item) => item !== namespace);
      }
      return [...prev, namespace];
    });
  };

  const toggleElement = (namespace: string, element: string) => {
    setExpandedElements((prev) => {
      const current = prev[namespace] || [];
      if (current.includes(element)) {
        return { ...prev, [namespace]: current.filter((item) => item !== element) };
      }
      return { ...prev, [namespace]: [...current, element] };
    });
  };

  const applyFunctionPermissions = async (nextPermissions: string[]) => {
    if (!selectedFunction) {
      return;
    }
    const payload = {
      type: selectedFunction.kind,
      securityObject: selectedFunction.name,
      permissions: uniquePermissions(nextPermissions)
    };
    try {
      await updateSecurityObjectPermissions({ variables: payload });
      setFunctionDraftPermissions(payload.permissions);
      await refetchSchema();
    } catch (error) {
      toast.error('Failed to update function permissions');
    }
  };

  const handleToggleFunctionPolicy = async (policy: string) => {
    const trimmed = `${policy || ''}`.trim();
    if (!trimmed) {
      return;
    }
    const isAssigned = functionPermissionsSet.has(trimmed);
    const next = new Set(functionAssignedPermissions);
    if (isAssigned) {
      next.delete(trimmed);
    }
    if (!isAssigned) {
      next.add(trimmed);
    }
    await applyFunctionPermissions(Array.from(next));
  };

  const handleAddFunctionPolicy = async () => {
    const policy = buildPolicyString([
      functionPolicyBuilder.access,
      functionPolicyBuilder.namespace,
      functionPolicyBuilder.element,
      functionPolicyBuilder.field,
      functionPolicyBuilder.scope
    ]);
    if (!policy) {
      toast.error('Policy is required');
      return;
    }
    if (functionPermissionsSet.has(policy)) {
      toast.error('Policy already assigned');
      return;
    }
    const next = new Set(functionAssignedPermissions);
    next.add(policy);
    await applyFunctionPermissions(Array.from(next));
  };

  const handleAddKnownFunctionPolicy = async (policy: string) => {
    const normalized = normalizePolicyString(policy || '');
    if (!normalized) {
      return;
    }
    if (functionPermissionsSet.has(normalized)) {
      return;
    }
    const next = new Set(functionAssignedPermissions);
    next.add(normalized);
    await applyFunctionPermissions(Array.from(next));
  };

  const handleAddFunctionElementPolicy = async (
    root: string,
    resource: string,
    action: string
  ) => {
    const policy = buildPolicyString([action, root, resource, '*', '*']);
    if (!policy) {
      return;
    }
    await handleAddKnownFunctionPolicy(policy);
  };

  const handleAddFunctionFieldPolicy = async (
    root: string,
    resource: string,
    field: string,
    action: string
  ) => {
    const policy = buildPolicyString([action, root, resource, field, '*']);
    if (!policy) {
      return;
    }
    await handleAddKnownFunctionPolicy(policy);
  };

  const focusOnFunctionPolicy = (policy: string) => {
    const { root, resource, field } = splitPolicy(policy);
    if (root && root !== '*') {
      setFunctionExpandedNamespaces((prev) => (prev.includes(root) ? prev : [...prev, root]));
    }
    if (root && root !== '*' && resource && resource !== '*') {
      setFunctionExpandedElements((prev) => {
        const current = prev[root] || [];
        if (current.includes(resource)) {
          return prev;
        }
        return { ...prev, [root]: [...current, resource] };
      });
    }
    setFunctionFocusedPolicy(policy);
    if (!root || root === '*') {
      setFunctionExpandedNamespaces([]);
      setFunctionExpandedElements({});
      return;
    }
    if (!resource || resource === '*') {
      return;
    }
    if (!field || field === '*') {
      return;
    }
  };

  const toggleFunctionNamespace = (namespace: string) => {
    setFunctionExpandedNamespaces((prev) => {
      if (prev.includes(namespace)) {
        return prev.filter((item) => item !== namespace);
      }
      return [...prev, namespace];
    });
  };

  const toggleFunctionElement = (namespace: string, element: string) => {
    setFunctionExpandedElements((prev) => {
      const current = prev[namespace] || [];
      if (current.includes(element)) {
        return { ...prev, [namespace]: current.filter((item) => item !== element) };
      }
      return { ...prev, [namespace]: [...current, element] };
    });
  };

  const handleSaveGroupDetails = async () => {
    const aliasName = aliasDraft.alias.trim();
    if (!aliasName) {
      toast.error('Group name is required');
      return;
    }
    await applyPermissions(selectedPermissions);
  };

  const handleDeleteGroup = async () => {
    if (!aliasDraft.id) {
      return;
    }
    try {
      await deleteAlias({ variables: { aliasId: aliasDraft.id } });
      await refetchAliases();
      setSelectedAlias(null);
      setAliasDraft({ id: 0, alias: '', type: 'group', description: '', policies: [] });
    } catch (error) {
      toast.error('Failed to delete group');
    }
  };

  const handleNewGroup = () => {
    setIsNewGroup(true);
    setSelectedAlias(null);
    setAliasDraft({ id: 0, alias: '', type: 'group', description: '', policies: [] });
    setIsListView(false);
  };

  const handleSelectGroup = (aliasName: string) => {
    setSelectedAlias(aliasName);
    setIsNewGroup(false);
    setIsListView(false);
  };

  const groupIdSuffix = toIdSegment(isNewGroup ? 'new-group' : selectedAlias || 'group');
  const selectedAliasType = getDraftAliasType(aliasDraft.alias, aliasDraft.type);
  const isLockedOrganizationalAlias = !isNewGroup && isOrganizationalAliasName(aliasDraft.alias);
  const canDeleteGroup = typeof aliasDraft.id === 'number' && aliasDraft.id > 0;
  const canEditGroup = aliasDraft.alias.trim().length > 0;
  const showGroupList = isListView;
  const showGroupDetail = !isListView;
  const canEditFunction = Boolean(selectedFunction) && !selectedIsUnauth && !selectedIsMail;
  const focusedParts = focusedPolicy
    ? splitPolicy(focusedPolicy)
    : { root: '', resource: '', field: '', action: '' };
  const focusedNamespace = focusedParts.root;
  const focusedElement = focusedParts.resource;
  const focusedField = focusedParts.field;

  const functionFocusedParts = functionFocusedPolicy
    ? splitPolicy(functionFocusedPolicy)
    : { root: '', resource: '', field: '', action: '' };
  const functionFocusedNamespace = functionFocusedParts.root;
  const functionFocusedElement = functionFocusedParts.resource;

  const handleBackToList = () => {
    setSelectedAlias(null);
    setIsNewGroup(false);
    setAliasDraft({ id: 0, alias: '', type: 'group', description: '', policies: [] });
    setIsListView(true);
  };

  if (aliasLoading) {
    return <div className="mx-auto max-w-[1600px] px-4 py-4 text-[var(--neutral-gray-80)]">Loading security manager...</div>;
  }

  if (aliasError) {
    return (
      <div className="mx-auto max-w-[1600px] px-4 py-4 text-[var(--semantic-error)]">
        Error: {aliasError.message}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1600px] space-y-4 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-[var(--neutral-gray-120)]">Security Manager</h1>
          {activeTab === 'summary' && (
            <p className="text-sm text-[var(--neutral-gray-80)]">Monitor security coverage for GraphQL functions.</p>
          )}
          {activeTab === 'functions' && !selectedFunction && (
            <p className="text-sm text-[var(--neutral-gray-80)]">Select a function to manage policies.</p>
          )}
          {activeTab === 'functions' && selectedFunction && (
            <div className="flex items-center gap-2 text-sm text-[var(--neutral-gray-80)]">
              <Button
                id="security-manager-breadcrumb-functions"
                type="button"
                variant="link"
                size="sm"
                onClick={() => setSelectedFunctionId(null)}
                className="h-auto p-0 text-sm font-medium"
              >
                Functions
              </Button>
              <ChevronRight className="h-4 w-4 text-[var(--neutral-gray-70)]" />
              <span>{selectedFunction.name}</span>
            </div>
          )}
          {activeTab === 'policies' && (
            <p className="text-sm text-[var(--neutral-gray-80)]">Review and add policies for functions.</p>
          )}
          {activeTab === 'groups' && showGroupList && (
            <p className="text-sm text-[var(--neutral-gray-80)]">Select a group to manage policies.</p>
          )}
          {activeTab === 'groups' && showGroupDetail && (
            <div className="flex items-center gap-2 text-sm text-[var(--neutral-gray-80)]">
              <Button
                id="security-manager-breadcrumb-groups"
                type="button"
                variant="link"
                size="sm"
                onClick={handleBackToList}
                className="h-auto p-0 text-sm font-medium"
              >
                Groups
              </Button>
              <ChevronRight className="h-4 w-4 text-[var(--neutral-gray-70)]" />
              <span>{isNewGroup ? 'New Group' : aliasDraft.alias || 'Group'}</span>
            </div>
          )}
        </div>
        {activeTab === 'groups' && showGroupList && (
          <Button id="security-manager-new-group-button" onClick={handleNewGroup} size="sm">
            <Plus className="mr-2 h-4 w-4" />
            New Group
          </Button>
        )}
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) =>
          setActiveTab(value as 'summary' | 'functions' | 'groups' | 'policies')
        }
        className="space-y-4"
      >
        <TabsList className="grid w-full max-w-[520px] grid-cols-4">
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="groups">Groups</TabsTrigger>
          <TabsTrigger value="functions">Functions</TabsTrigger>
          <TabsTrigger value="policies">Policies</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="space-y-4">
          {schemaLoading && (
            <div className="rounded-[var(--border-radius-large)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] p-4 text-sm text-[var(--neutral-gray-80)]">
              Loading schema...
            </div>
          )}
          {schemaError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {schemaError.message}
            </div>
          )}
          {!schemaLoading && !schemaError && (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px]">
              <div className="space-y-4">
                <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
                  <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] p-3 shadow-[var(--shadow-light)]">
                    <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">
                      Total Functions
                    </div>
                    <div className="text-2xl font-semibold text-[var(--neutral-gray-120)]">
                      {functionItems.length}
                    </div>
                  </div>
                  <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] p-3 shadow-[var(--shadow-light)]">
                    <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">Queries</div>
                    <div className="text-2xl font-semibold text-[var(--neutral-gray-120)]">
                      {kindCounts.query}
                    </div>
                  </div>
                  <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] p-3 shadow-[var(--shadow-light)]">
                    <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">
                      Mutations
                    </div>
                    <div className="text-2xl font-semibold text-[var(--neutral-gray-120)]">
                      {kindCounts.mutation}
                    </div>
                  </div>
                  <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] p-3 shadow-[var(--shadow-light)]">
                    <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">
                      Subscriptions
                    </div>
                    <div className="text-2xl font-semibold text-[var(--neutral-gray-120)]">
                      {kindCounts.subscription}
                    </div>
                  </div>
                <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] p-3 shadow-[var(--shadow-light)]">
                  <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">
                    Missing Entries
                  </div>
                  <div
                      className={cn(
                        'text-2xl font-semibold',
                        missingEntryCount > 0 ? 'text-red-600' : 'text-gray-900'
                      )}
                    >
                      {missingEntryCount}
                  </div>
                </div>
                <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] p-3 shadow-[var(--shadow-light)]">
                  <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">
                    Invalid Permissions
                  </div>
                  <div
                      className={cn(
                        'text-2xl font-semibold',
                        invalidPermissionCount > 0 ? 'text-red-600' : 'text-gray-900'
                      )}
                    >
                      {invalidPermissionCount}
                  </div>
                </div>
                  <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] p-3 shadow-[var(--shadow-light)]">
                    <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--azure-primary)]">
                      Unauthenticated
                    </div>
                    <div
                      className={cn(
                        'text-2xl font-semibold',
                        unauthenticatedEndpoints.length > 0 ? 'text-blue-600' : 'text-gray-900'
                      )}
                    >
                      {unauthenticatedEndpoints.length}
                    </div>
                  </div>
                  <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] p-3 shadow-[var(--shadow-light)]">
                    <div className="text-xs font-semibold uppercase tracking-[0.08em] text-purple-500">
                      Mail (Unauth)
                    </div>
                    <div
                      className={cn(
                        'text-2xl font-semibold',
                        mailEndpoints.length > 0 ? 'text-purple-600' : 'text-gray-900'
                      )}
                    >
                      {mailEndpoints.length}
                    </div>
                  </div>
                </div>

                <div className="rounded-[var(--border-radius-large)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] shadow-[var(--shadow-light)]">
                  <div className="flex items-center justify-between border-b border-[var(--neutral-gray-30)] px-4 py-3">
                    <div className="text-sm font-semibold text-[var(--neutral-gray-120)]">Functions</div>
                    <div className="text-xs text-[var(--neutral-gray-80)]">
                      {summaryFunctionItems.length} total
                    </div>
                  </div>
                  <ScrollArea className="h-[520px]" type="always">
                    <div className="divide-y divide-gray-100">
                      {summaryFunctionItems.map((item) => {
                        const isError = errorIdSet.has(item.id);
                        const isUnauth = isUnauthenticatedEndpoint(item);
                        const isMail = isMailEndpoint(item);
                        return (
                          <button
                            key={item.id}
                            id={toDomId('security-manager-summary-function', item.id)}
                            onClick={() => {
                              setSelectedFunctionId(item.id);
                              setActiveTab('functions');
                            }}
                            className={cn(
                              'grid w-full grid-cols-[110px_1fr_140px] items-center gap-3 px-4 py-2 text-left text-sm',
                              isError
                                ? 'bg-red-50'
                                : isUnauth
                                ? 'bg-blue-50'
                                : isMail
                                ? 'bg-purple-50'
                                : 'hover:bg-[var(--surface-muted)]'
                            )}
                          >
                            <span className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">
                              {KIND_LABELS[item.kind]}
                            </span>
                            <span
                              className={cn(
                                'font-semibold',
                                isError
                                  ? 'text-red-700'
                                  : isUnauth
                                  ? 'text-blue-800'
                                  : isMail
                                  ? 'text-purple-800'
                                  : 'text-gray-900'
                              )}
                            >
                              {item.name}
                            </span>
                            <span className="text-right text-xs font-mono text-[var(--neutral-gray-80)]">
                              {item.returnType || 'void'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </div>
              </div>

              <div className="rounded-[var(--border-radius-large)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] shadow-[var(--shadow-light)]">
                <div className="flex items-center justify-between border-b border-[var(--neutral-gray-30)] px-4 py-3">
                  <div className="text-sm font-semibold text-[var(--neutral-gray-120)]">Issues</div>
                  <div
                    className={cn(
                      'text-xs font-semibold',
                      errorEndpoints.length > 0 ? 'text-red-600' : 'text-[var(--neutral-gray-80)]'
                    )}
                  >
                    {errorEndpoints.length} issue(s)
                  </div>
                </div>
                {errorEndpoints.length === 0 && (
                  <div className="px-4 py-6 text-sm text-[var(--neutral-gray-80)]">No issues detected.</div>
                )}
                {errorEndpoints.length > 0 && (
                  <ScrollArea className="h-[720px]">
                    <div className="space-y-2 px-4 py-3">
                      {errorEndpoints.map((entry) => (
                        <button
                          key={entry.id}
                          id={toDomId('security-manager-summary-issue', entry.id)}
                          onClick={() => {
                            setSelectedFunctionId(entry.id);
                            setActiveTab('functions');
                          }}
                          className="w-full rounded-md border border-red-200 bg-red-50 px-3 py-2 text-left"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold uppercase text-red-700">
                              {KIND_LABELS[entry.kind]}
                            </span>
                            <span className="text-sm font-semibold text-red-900">
                              {entry.name}
                            </span>
                          </div>
                          <div className="text-xs text-red-700">
                            {entry.reasons.join(' • ')}
                          </div>
                          {Array.isArray(entry.permissions) && entry.permissions.length > 0 && (
                            <div className="mt-1 text-xs font-mono text-red-800">
                              {entry.permissions.join(', ')}
                            </div>
                          )}
                          {(!entry.permissions || entry.permissions.length === 0) && (
                            <div className="mt-1 text-xs text-red-700">
                              No permissions assigned.
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="functions" className="space-y-3">
          {schemaLoading && (
            <div className="rounded-[var(--border-radius-large)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] p-4 text-sm text-[var(--neutral-gray-80)]">
              Loading schema...
            </div>
          )}
          {schemaError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {schemaError.message}
            </div>
          )}
          {!schemaLoading && !schemaError && (
            <div className="space-y-4">
              {!selectedFunction && (
                <div className="rounded-[var(--border-radius-large)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] shadow-[var(--shadow-light)]">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--neutral-gray-30)] px-3 py-3">
                    <div>
                      <div className="text-sm font-semibold text-[var(--neutral-gray-120)]">Functions</div>
                      <div className="text-xs text-[var(--neutral-gray-80)]">
                        {functionList.length} definitions found
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="relative w-[220px]">
                        <Search className="absolute left-2.5 top-2 h-4 w-4 text-[var(--neutral-gray-70)]" />
                        <Input
                          id="security-manager-function-search-input"
                          value={functionSearch}
                          onChange={(event) => setFunctionSearch(event.target.value)}
                          placeholder="Filter functions..."
                          className="h-8 pl-8 text-xs"
                        />
                      </div>
                      <div className="flex gap-1 rounded-[var(--border-radius-medium)] bg-[var(--surface-muted)] p-1">
                        {(['query', 'mutation', 'subscription'] as const).map((kind) => (
                          <button
                            key={kind}
                            id={`security-manager-filter-${kind}-button`}
                            onClick={() =>
                              setSelectedKind(selectedKind === kind ? null : kind)
                            }
                            className={cn(
                              'flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold transition-colors',
                              selectedKind === kind
                                ? 'bg-[var(--surface-panel-solid)] text-[var(--neutral-gray-120)] shadow-[var(--shadow-light)]'
                                : 'text-[var(--neutral-gray-80)] hover:text-[var(--neutral-gray-120)]'
                            )}
                          >
                            {KIND_ICONS[kind]}
                            <span>{kindCounts[kind]}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="h-[720px] overflow-y-auto pr-2">
                    <div className="space-y-1 px-3 py-2">
                      {functionList.length === 0 && (
                        <div className="px-2 py-1 text-xs text-[var(--neutral-gray-80)]">
                          No functions match the current filters.
                        </div>
                      )}
                      {functionList.length > 0 &&
                        functionList.map((item) => {
                          const isError = errorIdSet.has(item.id);
                          const isUnauth = isUnauthenticatedEndpoint(item);
                          const isMail = isMailEndpoint(item);
                          return (
                            <button
                              key={item.id}
                              id={toDomId('security-manager-function', item.id)}
                              onClick={() => setSelectedFunctionId(item.id)}
                              className={cn(
                                'grid w-full grid-cols-[110px_1fr_160px] items-center gap-3 rounded-sm px-3 py-2 text-left text-xs',
                                isError
                                  ? 'bg-red-50'
                                  : isUnauth
                                  ? 'bg-blue-50'
                                  : isMail
                                  ? 'bg-purple-50'
                                  : 'hover:bg-[var(--surface-muted)]'
                              )}
                            >
                              <span className="flex items-center gap-2 text-xs font-semibold text-[var(--neutral-gray-100)]">
                                <span
                                  className={cn(
                                    'rounded-full px-2 py-0.5 text-xs uppercase',
                                    item.kind === 'query'
                                      ? 'bg-emerald-50 text-emerald-700'
                                      : item.kind === 'mutation'
                                      ? 'bg-amber-50 text-amber-700'
                                      : 'bg-purple-50 text-purple-700'
                                  )}
                                >
                                  {KIND_LABELS[item.kind]}
                                </span>
                                {item.deprecated && (
                                  <Badge variant="destructive" className="h-5 px-1 text-xs">
                                    Deprecated
                                  </Badge>
                                )}
                              </span>
                              <span
                                className={cn(
                                  'font-semibold',
                                  isError
                                    ? 'text-red-700'
                                    : isUnauth
                                    ? 'text-blue-800'
                                    : isMail
                                    ? 'text-purple-800'
                                    : 'text-gray-900'
                                )}
                              >
                                {item.name}
                                {isError && (
                                  <span className="ml-2 text-xs font-semibold text-red-600">
                                    Issue
                                  </span>
                                )}
                                {isUnauth && (
                                  <Badge className="ml-2 h-5 px-1 text-xs bg-blue-100 text-blue-800 border border-blue-200">
                                    Unauth
                                  </Badge>
                                )}
                                {isMail && (
                                  <Badge className="ml-2 h-5 px-1 text-xs bg-purple-100 text-purple-800 border border-purple-200">
                                    Mail
                                  </Badge>
                                )}
                                <span className="mt-1 block text-xs text-[var(--neutral-gray-80)]">
                                  {getPathSegments(item).join(' / ')}
                                </span>
                              </span>
                              <span className="text-right text-xs font-mono text-[var(--neutral-gray-80)]">
                                {item.returnType || 'void'}
                              </span>
                            </button>
                          );
                        })}
                    </div>
                  </div>
                </div>
              )}
              {selectedFunction && (
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_420px]">
                  <main className="space-y-3">
                    <div className="rounded-[var(--border-radius-large)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] shadow-[var(--shadow-light)]">
                      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[var(--neutral-gray-30)] px-4 py-3">
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">
                            {KIND_LABELS[selectedFunction.kind]} Function
                          </div>
                          <div className="text-base font-semibold text-[var(--neutral-gray-120)]">
                            {selectedFunction.name}
                          </div>
                          <div className="text-xs text-[var(--neutral-gray-80)]">
                            {getPathSegments(selectedFunction).join(' / ')}
                          </div>
                          <div className="mt-1 text-xs text-[var(--neutral-gray-80)]">
                            Resource:{' '}
                            <span className="font-mono text-[var(--neutral-gray-100)]">
                              {selectedFunction.resourcePath || '—'}
                            </span>{' '}
                            • Verb:{' '}
                            <span className="font-mono text-[var(--neutral-gray-100)]">
                              {selectedFunction.verb || '—'}
                            </span>{' '}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge
                            variant="outline"
                            className={cn(
                              'text-xs capitalize',
                              selectedFunction.kind === 'query' &&
                                'border-emerald-200 bg-emerald-50 text-emerald-700',
                              selectedFunction.kind === 'mutation' &&
                                'border-amber-200 bg-amber-50 text-amber-700',
                              selectedFunction.kind === 'subscription' &&
                                'border-purple-200 bg-purple-50 text-purple-700'
                            )}
                          >
                            {selectedFunction.kind}
                          </Badge>
                          {selectedIsUnauth && (
                            <Badge className="text-xs bg-blue-50 text-blue-700 border border-blue-200">
                              Unauthenticated
                            </Badge>
                          )}
                          {selectedIsMail && (
                            <Badge className="text-xs bg-purple-50 text-purple-700 border border-purple-200">
                              Mail
                            </Badge>
                          )}
                          {selectedFunctionErrors && (
                            <Badge variant="destructive" className="text-xs">
                              Issue
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="px-4 py-3 space-y-3">
                        {selectedFunctionErrors && (
                          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                            <div className="font-semibold text-red-900">Permission issues</div>
                            <div className="text-xs text-red-700">
                              {selectedFunctionErrors.reasons.join(' • ')}
                            </div>
                          </div>
                        )}
                        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_220px]">
                          <div className="space-y-1">
                            <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">
                              Description
                            </div>
                            <div className="text-sm text-[var(--neutral-gray-100)]">
                              {selectedFunction.description ||
                                'No description provided for this function.'}
                            </div>
                          </div>
                          <div className="space-y-1">
                            <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">
                              Return Type
                            </div>
                            <div className="text-sm">
                              {selectedFunction.returnType ? (
                                <Link
                                  id={toDomId(
                                    'security-manager-return-type',
                                    selectedFunction.returnType
                                  )}
                                  to={`/admin/dev/types?type=${encodeURIComponent(
                                    normalizeTypeName(selectedFunction.returnType)
                                  )}`}
                                  className="font-mono text-blue-600 hover:underline"
                                >
                                  {selectedFunction.returnType}
                                </Link>
                              ) : (
                                <span className="text-[var(--neutral-gray-80)]">Void</span>
                              )}
                            </div>
                            <div className="text-xs text-[var(--neutral-gray-80)]">
                              Effective policies apply to the return type.
                            </div>
                          </div>
                        </div>
                        {(selectedIsUnauth || selectedIsMail) && (
                          <div
                            className={cn(
                              'rounded-md p-3 text-sm',
                              selectedIsUnauth
                                ? 'border border-blue-200 bg-blue-50 text-blue-800'
                                : 'border border-purple-200 bg-purple-50 text-purple-800'
                            )}
                          >
                            {selectedIsUnauth &&
                              'This function is unauthenticated and bypasses the permissions system. Editing policies is disabled.'}
                            {selectedIsMail &&
                              'Mail endpoints are system-managed and bypass the permissions system. Editing policies is disabled.'}
                          </div>
                        )}
                        <div className="space-y-2">
                          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">
                            Parameters
                          </div>
                          {formatParameters(selectedFunction.parameters).length === 0 && (
                            <div className="text-xs text-[var(--neutral-gray-80)]">No parameters required.</div>
                          )}
                          {formatParameters(selectedFunction.parameters).length > 0 && (
                            <div className="overflow-hidden rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)]">
                              <table className="w-full text-sm">
                                <thead className="bg-[var(--surface-muted)] text-xs uppercase text-[var(--neutral-gray-80)]">
                                  <tr>
                                    <th className="px-3 py-2 text-left">Name</th>
                                    <th className="px-3 py-2 text-left">Type</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {formatParameters(selectedFunction.parameters).map((param) => {
                                    const typeName = toTypeName(param.type);
                                    return (
                                      <tr key={param.name}>
                                        <td className="px-3 py-2 font-medium text-[var(--neutral-gray-120)]">
                                          {param.name}
                                        </td>
                                        <td className="px-3 py-2 font-mono text-xs text-[var(--neutral-gray-90)]">
                                          {typeName ? (
                                            <Link
                                              id={toDomId(
                                                'security-manager-param-type',
                                                param.name
                                              )}
                                              to={`/admin/dev/types?type=${encodeURIComponent(
                                                typeName
                                              )}`}
                                              className="text-blue-600 hover:underline"
                                            >
                                              {param.type}
                                            </Link>
                                          ) : (
                                            param.type
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                          <div className="text-xs text-[var(--neutral-gray-80)]">
                            Effective policies apply to all parameters.
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">
                            Effective Policies
                          </div>
                          {functionAssignedPolicies.length === 0 && (
                            <div className="text-xs text-[var(--neutral-gray-80)]">No policies assigned.</div>
                          )}
                          {functionAssignedPolicies.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {functionAssignedPolicies.map((policy) => (
                                <span
                                  key={policy}
                                  className="rounded bg-[var(--surface-muted)] px-2 py-1 text-xs font-mono text-[var(--neutral-gray-100)]"
                                >
                                  {policy}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap items-end gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-[var(--neutral-gray-80)]">Namespace</span>
                            <Select
                              value={functionPolicyBuilder.namespace}
                              onValueChange={(value) =>
                                setFunctionPolicyBuilder((prev) => ({
                                  ...prev,
                                  namespace: value
                                }))
                              }
                            >
                              <SelectTrigger
                                id="security-manager-function-policy-namespace-select"
                                className="h-7 w-[140px] text-xs"
                              >
                                <SelectValue placeholder="Namespace" />
                              </SelectTrigger>
                              <SelectContent>
                                {namespaceOptions.map((value) => (
                                  <SelectItem
                                    key={value}
                                    value={value}
                                    id={buildId(
                                      'security-manager-function-policy-namespace',
                                      value
                                    )}
                                  >
                                    {value}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-[var(--neutral-gray-80)]">Resource</span>
                            <div className="flex flex-col">
                              <Input
                                id="security-manager-function-policy-element-input"
                                className="h-7 w-[180px] text-xs"
                                list="security-manager-function-policy-element-datalist"
                                value={functionPolicyBuilder.element}
                                placeholder="Resource (free text)"
                                onChange={(e) =>
                                  setFunctionPolicyBuilder((prev) => ({
                                    ...prev,
                                    element: e.target.value
                                  }))
                                }
                              />
                              <datalist id="security-manager-function-policy-element-datalist">
                                {functionAvailableElements.map((value) => (
                                  <option key={value} value={value} />
                                ))}
                              </datalist>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-[var(--neutral-gray-80)]">Action</span>
                            <Select
                              value={functionPolicyBuilder.access}
                              onValueChange={(value) =>
                                setFunctionPolicyBuilder((prev) => ({
                                  ...prev,
                                  access: value
                                }))
                              }
                            >
                              <SelectTrigger
                                id="security-manager-function-policy-access-select"
                                className="h-7 w-[92px] text-xs"
                              >
                                <SelectValue placeholder="Action" />
                              </SelectTrigger>
                              <SelectContent>
                                {policySegments.access.map((value) => (
                                  <SelectItem
                                    key={value}
                                    value={value}
                                    id={buildId('security-manager-function-policy-access', value)}
                                  >
                                    {value}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {functionPolicyBuilder.namespace === 'type' && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-[var(--neutral-gray-80)]">Field</span>
                              <Select
                                value={functionPolicyBuilder.field}
                                onValueChange={(value) =>
                                  setFunctionPolicyBuilder((prev) => ({
                                    ...prev,
                                    field: value
                                  }))
                                }
                              >
                                <SelectTrigger
                                  id="security-manager-function-policy-field-select"
                                  className="h-7 w-[140px] text-xs"
                                >
                                  <SelectValue placeholder="Field" />
                                </SelectTrigger>
                                <SelectContent>
                                  {functionAvailableFields.map((value) => (
                                    <SelectItem
                                      key={value}
                                      value={value}
                                      id={buildId(
                                        'security-manager-function-policy-field',
                                        value
                                      )}
                                    >
                                      {value}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          <Button
                            id="security-manager-function-policy-add-button"
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleAddFunctionPolicy}
                            disabled={!canEditFunction || savingFunctionPermissions}
                            className="h-7 text-xs"
                          >
                            <Plus className="mr-1 h-3.5 w-3.5" />
                            Add
                          </Button>
                        </div>
                        <div className="flex items-center justify-between border-t border-[var(--neutral-gray-30)] pt-3">
                          <div className="text-sm font-semibold text-[var(--neutral-gray-120)]">Policies</div>
                          <div className="text-xs text-[var(--neutral-gray-80)]">
                            Assigned {functionAssignedPolicies.length}
                          </div>
                        </div>
                        {functionAssignedPolicies.length === 0 && (
                          <div className="text-xs text-[var(--neutral-gray-80)]">No policies assigned.</div>
                        )}
                        {functionAssignedPolicies.length > 0 && (
                          <ScrollArea className="h-[360px] pr-2">
                            <div className="space-y-1">
                              {functionAssignedPolicies.map((policy) => (
                                <div
                                  key={policy}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => focusOnFunctionPolicy(policy)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      focusOnFunctionPolicy(policy);
                                    }
                                  }}
                                  className={cn(
                                    'flex items-center justify-between gap-2 rounded-sm border-b border-[var(--neutral-gray-20)] px-1 py-1 text-xs hover:bg-[var(--surface-muted)]',
                                    functionFocusedPolicy === policy
                                      ? 'bg-blue-50 border-blue-100'
                                      : ''
                                  )}
                                >
                                  <span className="font-mono text-[var(--neutral-gray-120)]">{policy}</span>
                                  <Button
                                    id={buildId(
                                      'security-manager-function-policy-remove',
                                      policy
                                    )}
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleToggleFunctionPolicy(policy);
                                    }}
                                    className="h-7 px-2 text-[var(--neutral-gray-80)] hover:text-[var(--semantic-error)]"
                                    disabled={!canEditFunction}
                                    aria-label={`Remove ${policy}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        )}
                      </div>
                    </div>
                  </main>
                  <aside className="space-y-3">
                    <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
                      <div className="text-sm font-semibold text-gray-900">Policy Tree</div>
                      <div className="text-xs text-gray-500">Add by namespace and resource</div>
                      <ScrollArea className="mt-3 h-[520px] pr-2">
                        <div className="space-y-2">
                          {policyTree.map((entry) => {
                            const namespaceExpanded =
                              functionExpandedNamespaces.includes(entry.root);
                            const isNamespaceFocused =
                              functionFocusedNamespace === entry.root;
                            const isTypeRoot = entry.root === 'type';
                            return (
                              <div
                                key={entry.root}
                                className={cn(
                                  'rounded-sm border border-gray-100 bg-gray-50 px-2 py-2',
                                  isNamespaceFocused ? 'border-blue-200 bg-blue-50' : ''
                                )}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <button
                                    id={buildId(
                                      'security-manager-function-tree-namespace',
                                      entry.root
                                    )}
                                    onClick={() => toggleFunctionNamespace(entry.root)}
                                    className="flex items-center gap-2 text-xs font-semibold text-gray-900"
                                  >
                                    <ChevronRight
                                      className={cn(
                                        'h-3.5 w-3.5 text-gray-600 transition-transform',
                                        namespaceExpanded ? 'rotate-90' : ''
                                      )}
                                    />
                                    <span>{entry.root}</span>
                                  </button>
                                </div>
                                {namespaceExpanded && (
                                  <div className="mt-2 space-y-1 pl-4">
                                    {entry.resources.map((resource) => {
                                      const elementExpanded = (
                                        functionExpandedElements[entry.root] || []
                                      ).includes(resource.resource);
                                      const isElementFocused =
                                        isNamespaceFocused &&
                                        functionFocusedElement === resource.resource;
                                      return (
                                        <div
                                          key={`${entry.root}-${resource.resource}`}
                                          className={cn(
                                            'rounded-sm bg-white px-2 py-1',
                                            isElementFocused
                                              ? 'border border-blue-200 bg-blue-50'
                                              : ''
                                          )}
                                        >
                                          <div className="flex items-center justify-between gap-2">
                                            <button
                                              id={buildId(
                                                'security-manager-function-tree-element',
                                                `${entry.root}-${resource.resource}`
                                              )}
                                              onClick={() =>
                                                toggleFunctionElement(
                                                  entry.root,
                                                  resource.resource
                                                )
                                              }
                                              className="flex items-center gap-2 text-xs font-semibold text-gray-800"
                                            >
                                              <ChevronRight
                                                className={cn(
                                                  'h-3.5 w-3.5 text-gray-600 transition-transform',
                                                  elementExpanded ? 'rotate-90' : ''
                                                )}
                                              />
                                              <span
                                                className={
                                                  isElementFocused ? 'text-blue-800' : ''
                                                }
                                              >
                                                {resource.resource}
                                              </span>
                                            </button>
                                            {!isTypeRoot && (
                                              <div className="flex flex-wrap items-center gap-1">
                                                {resource.actions.length === 0 && (
                                                  <span className="text-xs text-gray-500">
                                                    No actions
                                                  </span>
                                                )}
                                                {resource.actions.map((action) => {
                                                  const policy = buildPolicyString([
                                                    action,
                                                    entry.root,
                                                    resource.resource,
                                                    '*',
                                                    '*'
                                                  ]);
                                                  const already =
                                                    functionPermissionsSet.has(policy);
                                                  return (
                                                    <button
                                                      key={action}
                                                      id={buildId(
                                                        'security-manager-function-tree-add-action',
                                                        `${entry.root}-${resource.resource}-${action}`
                                                      )}
                                                      onClick={() =>
                                                        handleAddFunctionElementPolicy(
                                                          entry.root,
                                                          resource.resource,
                                                          action
                                                        )
                                                      }
                                                      disabled={!canEditFunction || already}
                                                      className="rounded px-2 py-1 text-xs font-semibold text-blue-700 hover:text-blue-800 disabled:opacity-30"
                                                      aria-label={`Add ${action} ${entry.root}:${resource.resource}`}
                                                    >
                                                      <span className="underline decoration-dotted">
                                                        {action}
                                                      </span>
                                                    </button>
                                                  );
                                                })}
                                              </div>
                                            )}
                                          </div>
                                          {isTypeRoot && elementExpanded && (
                                            <div className="mt-1 space-y-1 pl-5">
                                              {resource.fields.length === 0 && (
                                                <div className="text-xs text-gray-500">
                                                  No fields.
                                                </div>
                                              )}
                                              {resource.fields.map((field) => (
                                                <div
                                                  key={`${entry.root}-${resource.resource}-${field.field}`}
                                                  className="flex items-center justify-between gap-2 text-xs"
                                                >
                                                  <span className="font-mono text-gray-800">
                                                    {field.field}
                                                  </span>
                                                  <div className="flex items-center gap-1">
                                                    {field.actions.length === 0 && (
                                                      <span className="text-xs text-gray-500">
                                                        No actions
                                                      </span>
                                                    )}
                                                    {field.actions.map((action) => {
                                                      const policy = buildPolicyString([
                                                        action,
                                                        entry.root,
                                                        resource.resource,
                                                        field.field,
                                                        '*'
                                                      ]);
                                                      const already =
                                                        functionPermissionsSet.has(policy);
                                                      return (
                                                        <button
                                                          key={action}
                                                          id={buildId(
                                                            'security-manager-function-tree-add-field-action',
                                                            `${entry.root}-${resource.resource}-${field.field}-${action}`
                                                          )}
                                                          onClick={() =>
                                                            handleAddFunctionFieldPolicy(
                                                              entry.root,
                                                              resource.resource,
                                                              field.field,
                                                              action
                                                            )
                                                          }
                                                          disabled={!canEditFunction || already}
                                                          className="rounded px-2 py-1 text-xs font-semibold text-blue-700 hover:text-blue-800 disabled:opacity-30"
                                                          aria-label={`Add ${action} ${entry.root}:${resource.resource}:${field.field}`}
                                                        >
                                                          <span className="underline decoration-dotted">
                                                            {action}
                                                          </span>
                                                        </button>
                                                      );
                                                    })}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                    </div>
                  </aside>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="policies" className="space-y-3">
          {schemaLoading && (
            <div className="rounded-md border border-gray-200 bg-white p-4 text-sm text-gray-600">
              Loading schema...
            </div>
          )}
          {schemaError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {schemaError.message}
            </div>
          )}
          {!schemaLoading && !schemaError && (
            <div className="rounded-md border border-gray-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900">Policy Library</div>
                  <div className="text-xs text-gray-500">
                    All known policies. Use them in policy builders.
                  </div>
                </div>
              </div>
              <div className="px-4 py-4 space-y-3">
                <div className="grid gap-2">
                  <Input
                    id="security-manager-policy-library-search"
                    value={policyLibrarySearch}
                    onChange={(event) => setPolicyLibrarySearch(event.target.value)}
                    placeholder="Filter policies..."
                    className="h-8 text-xs"
                  />
                </div>
                {filteredPolicyLibrary.length === 0 && (
                  <div className="text-xs text-gray-500">No policies found.</div>
                )}
                {filteredPolicyLibrary.length > 0 && (
                  <ScrollArea className="h-[560px] pr-2">
                    <div className="space-y-1">
                      {filteredPolicyLibrary.map((policy) => (
                        <div
                          key={policy}
                          className="flex items-center justify-between gap-2 rounded-sm border border-gray-100 px-2 py-1 text-xs"
                        >
                          <span className="font-mono text-gray-700">{policy}</span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="groups">
          {showGroupList && (
        <div className="rounded-md border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
            <div className="text-sm font-semibold text-gray-900">Groups</div>
            <Input
              id="security-manager-group-search-input"
              value={groupSearch}
              onChange={(event) => setGroupSearch(event.target.value)}
              placeholder="Filter"
              className="h-8 max-w-[180px] text-xs"
            />
          </div>
          <ScrollArea className="h-[720px]">
            <div className="space-y-1 px-3 py-2">
              {aliasList.map((alias) => (
                <button
                  key={alias.alias}
                  id={buildId('security-manager-group-item', alias.alias)}
                  onClick={() => handleSelectGroup(alias.alias)}
                  className="flex w-full items-center gap-2 rounded-sm py-1 pl-3 pr-2 text-left text-xs text-gray-700 hover:bg-gray-50"
                >
                  <Shield className="h-3.5 w-3.5 text-gray-400" />
                  <span className="flex min-w-0 items-center gap-2 truncate">
                    <span className="truncate">{getAliasDisplayLabel(alias)}</span>
                    <span className={cn('rounded px-1 text-[10px] uppercase', aliasTypeBadgeClass[resolveAliasType(alias)])}>
                      {aliasTypeLabel[resolveAliasType(alias)]}
                    </span>
                  </span>
                </button>
              ))}
              {aliasList.length === 0 && (
                <div className="px-2 py-1 text-xs text-gray-500">No groups.</div>
              )}
            </div>
          </ScrollArea>
        </div>
      )}

      {showGroupDetail && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_420px]">
          <main className="space-y-3">
            <div className="rounded-md border border-gray-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-200 px-4 py-3">
                <div>
                  <div className="text-xs font-semibold uppercase text-gray-500">
                    {isNewGroup ? 'New Group' : 'Group'}
                  </div>
                  {!isNewGroup && (
                    <div className="text-base font-semibold text-gray-900">{getAliasDisplayLabel(aliasDraft)}</div>
                  )}
                  {isNewGroup && (
                    <Input
                      id={`security-manager-${groupIdSuffix}-name-input`}
                      value={aliasDraft.alias}
                      onChange={(event) =>
                        setAliasDraft((prev) => ({ ...prev, alias: event.target.value }))
                      }
                      placeholder="Group name"
                      className="mt-2 h-8 text-xs"
                    />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    id={`security-manager-${groupIdSuffix}-save-button`}
                    onClick={handleSaveGroupDetails}
                    disabled={savingAlias}
                    size="sm"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    Save
                  </Button>
                  {canDeleteGroup && (
                    <Button
                      id={`security-manager-${groupIdSuffix}-delete-button`}
                      variant="destructive"
                      onClick={handleDeleteGroup}
                      disabled={deletingAlias}
                      size="sm"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>

              <div className="px-4 py-3 space-y-3">
                <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
                  <Input
                    id={`security-manager-${groupIdSuffix}-description-input`}
                    value={aliasDraft.description || ''}
                    onChange={(event) =>
                      setAliasDraft((prev) => ({ ...prev, description: event.target.value }))
                    }
                    placeholder="Short description"
                    className="h-8 text-xs"
                  />
                  <Select
                    value={selectedAliasType}
                    onValueChange={(value) =>
                      setAliasDraft((prev) => ({
                        ...prev,
                        type: (normalizeAliasType(value) || 'group') as AliasType
                      }))
                    }
                    disabled={selectedAliasType === 'organizational'}
                  >
                    <SelectTrigger
                      id={`security-manager-${groupIdSuffix}-type-select`}
                      className="h-8 w-[180px] text-xs"
                    >
                      <SelectValue placeholder="Alias type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="group">Group</SelectItem>
                      <SelectItem value="policy">Feature</SelectItem>
                      {selectedAliasType === 'organizational' && (
                        <SelectItem value="organizational">Organizational</SelectItem>
                      )}
                      <SelectItem value="legacy">Legacy</SelectItem>
                      <SelectItem value="hidden">Hidden</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {isLockedOrganizationalAlias && (
                  <div className="text-xs text-gray-500">
                    Organizational aliases come from the active organization and role option lists.
                  </div>
                )}

                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Namespace</span>
                    <Select
                      value={policyBuilder.namespace}
                      onValueChange={(value) =>
                        setPolicyBuilder((prev) => ({ ...prev, namespace: value }))
                      }
                    >
                      <SelectTrigger
                        id="security-manager-policy-namespace-select"
                        className="h-7 w-[140px] text-xs"
                      >
                        <SelectValue placeholder="Namespace" />
                      </SelectTrigger>
                      <SelectContent>
                        {namespaceOptions.map((value) => (
                          <SelectItem
                            key={value}
                            value={value}
                            id={buildId('security-manager-policy-namespace-item', value)}
                          >
                            {value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Resource</span>
                    <div className="flex flex-col">
                      <Input
                        id="security-manager-policy-element-input"
                        className="h-7 w-[180px] text-xs"
                        list="security-manager-policy-element-datalist"
                        value={policyBuilder.element}
                        placeholder="Resource (free text)"
                        onChange={(e) =>
                          setPolicyBuilder((prev) => ({ ...prev, element: e.target.value }))
                        }
                      />
                      <datalist id="security-manager-policy-element-datalist">
                        {availableElements.map((value) => (
                          <option key={value} value={value} />
                        ))}
                      </datalist>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Action</span>
                    <Select
                      value={policyBuilder.access}
                      onValueChange={(value) =>
                        setPolicyBuilder((prev) => ({ ...prev, access: value }))
                      }
                    >
                      <SelectTrigger
                        id="security-manager-policy-access-select"
                        className="h-7 w-[92px] text-xs"
                      >
                        <SelectValue placeholder="Action" />
                      </SelectTrigger>
                      <SelectContent>
                        {policySegments.access.map((value) => (
                          <SelectItem
                            key={value}
                            value={value}
                            id={buildId('security-manager-policy-access-item', value)}
                          >
                            {value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {policyBuilder.namespace === 'type' && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Field</span>
                      <Select
                        value={policyBuilder.field}
                        onValueChange={(value) =>
                          setPolicyBuilder((prev) => ({ ...prev, field: value }))
                        }
                      >
                        <SelectTrigger
                          id="security-manager-policy-field-select"
                          className="h-7 w-[140px] text-xs"
                        >
                          <SelectValue placeholder="Field" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableFields.map((value) => (
                            <SelectItem
                              key={value}
                              value={value}
                              id={buildId('security-manager-policy-field-item', value)}
                            >
                              {value}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <button
                    id="security-manager-policy-add-button"
                    onClick={handleAddPolicy}
                    disabled={!canEditGroup}
                    className="inline-flex h-7 items-center justify-center rounded border border-gray-200 bg-gray-50 px-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    aria-label="Add policy to group"
                  >
                    <Plus className="mr-1 h-3.5 w-3.5 text-gray-600" />
                    Add
                  </button>
                </div>

                <div className="flex items-center justify-between border-t border-gray-200 pt-3">
                  <div className="text-sm font-semibold text-gray-900">Policies</div>
                  <div className="text-xs text-gray-500">Assigned {assignedPolicies.length}</div>
                </div>

                {assignedPolicies.length === 0 && (
                  <div className="text-xs text-gray-500">No policies assigned.</div>
                )}

                {assignedPolicies.length > 0 && (
                  <ScrollArea className="h-[520px] pr-2">
                    <div className="space-y-1">
                      {assignedPolicies.map((policy) => (
                        <div
                          key={policy}
                          role="button"
                          tabIndex={0}
                          onClick={() => focusOnPolicy(policy)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              focusOnPolicy(policy);
                            }
                          }}
                          className={cn(
                            'flex items-center justify-between gap-2 border-b border-gray-100 py-1 px-1 rounded-sm cursor-pointer hover:bg-gray-50',
                            focusedPolicy === policy ? 'bg-blue-50 border-blue-100' : ''
                          )}
                        >
                          <span className="text-xs font-mono text-gray-900">{policy}</span>
                          <button
                            id={buildId('security-manager-policy-remove', policy)}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleTogglePolicy(policy);
                            }}
                            className="text-gray-500 hover:text-red-600"
                            aria-label={`Remove ${policy}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </div>
          </main>

          <aside className="space-y-3">
            <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
              <div className="text-sm font-semibold text-gray-900">Policy Tree</div>
              <div className="text-xs text-gray-500">Add by namespace and resource</div>

              <ScrollArea className="mt-3 h-[520px] pr-2">
                <div className="space-y-2">
                  {policyTree.map((entry) => {
                    const namespaceExpanded = expandedNamespaces.includes(entry.root);
                    const isNamespaceFocused = focusedNamespace === entry.root;
                    const isTypeRoot = entry.root === 'type';
                    return (
                      <div
                        key={entry.root}
                        className={cn(
                          'rounded-sm border border-gray-100 bg-gray-50 px-2 py-2',
                          isNamespaceFocused ? 'border-blue-200 bg-blue-50' : ''
                        )}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <button
                            id={buildId('security-manager-tree-namespace-toggle', entry.root)}
                            onClick={() => toggleNamespace(entry.root)}
                            className="flex items-center gap-2 text-xs font-semibold text-gray-900"
                          >
                            <ChevronRight
                              className={cn(
                                'h-3.5 w-3.5 text-gray-600 transition-transform',
                                namespaceExpanded ? 'rotate-90' : ''
                              )}
                            />
                            <span>{entry.root}</span>
                          </button>
                        </div>

                        {namespaceExpanded && (
                          <div className="mt-2 space-y-1 pl-4">
                            {entry.resources.map((resource) => {
                              const elementExpanded =
                                (expandedElements[entry.root] || []).includes(resource.resource);
                              const isElementFocused =
                                isNamespaceFocused && focusedElement === resource.resource;
                              return (
                                <div
                                  key={`${entry.root}-${resource.resource}`}
                                  className={cn(
                                    'rounded-sm bg-white px-2 py-1',
                                    isElementFocused ? 'border border-blue-200 bg-blue-50' : ''
                                  )}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <button
                                      id={buildId(
                                        'security-manager-tree-element-toggle',
                                        `${entry.root}-${resource.resource}`
                                      )}
                                      onClick={() => toggleElement(entry.root, resource.resource)}
                                      className="flex items-center gap-2 text-xs font-semibold text-gray-800"
                                    >
                                      <ChevronRight
                                        className={cn(
                                          'h-3.5 w-3.5 text-gray-600 transition-transform',
                                          elementExpanded ? 'rotate-90' : ''
                                        )}
                                      />
                                      <span className={isElementFocused ? 'text-blue-800' : ''}>
                                        {resource.resource}
                                      </span>
                                    </button>
                                    {!isTypeRoot && (
                                      <div className="flex flex-wrap items-center gap-1">
                                        {resource.actions.length === 0 && (
                                          <span className="text-xs text-gray-500">
                                            No actions
                                          </span>
                                        )}
                                        {resource.actions.map((action) => {
                                          const policy = buildPolicyString([
                                            action,
                                            entry.root,
                                            resource.resource,
                                            '*',
                                            '*'
                                          ]);
                                          const already = permissionsSet.has(policy);
                                          return (
                                            <button
                                              key={action}
                                              id={buildId(
                                                'security-manager-tree-add-action',
                                                `${entry.root}-${resource.resource}-${action}`
                                              )}
                                              onClick={() =>
                                                handleAddElementPolicy(
                                                  entry.root,
                                                  resource.resource,
                                                  action
                                                )
                                              }
                                              disabled={!canEditGroup || already}
                                              className="rounded px-2 py-1 text-xs font-semibold text-blue-700 hover:text-blue-800 disabled:opacity-30"
                                              aria-label={`Add ${action} ${entry.root}:${resource.resource}`}
                                            >
                                              <span className="underline decoration-dotted">
                                                {action}
                                              </span>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>

                                  {isTypeRoot && elementExpanded && (
                                    <div className="mt-1 space-y-1 pl-5">
                                      {resource.fields.map((field) => {
                                        const isFieldFocused =
                                          isElementFocused && focusedField === field.field;
                                        return (
                                          <div
                                            key={`${entry.root}-${resource.resource}-${field.field}`}
                                            className={cn(
                                              'flex items-center justify-between gap-2 text-xs',
                                              isFieldFocused
                                                ? 'bg-blue-50 px-1 py-0.5 rounded'
                                                : ''
                                            )}
                                          >
                                            <span
                                              className={cn(
                                                'font-mono text-gray-800',
                                                isFieldFocused ? 'text-blue-800' : ''
                                              )}
                                            >
                                              {field.field}
                                            </span>
                                            <div className="flex items-center gap-1">
                                              {field.actions.length === 0 && (
                                                <span className="text-xs text-gray-500">
                                                  No actions
                                                </span>
                                              )}
                                              {field.actions.map((action) => {
                                                const policy = buildPolicyString([
                                                  action,
                                                  entry.root,
                                                  resource.resource,
                                                  field.field,
                                                  '*'
                                                ]);
                                                const already = permissionsSet.has(policy);
                                                return (
                                                  <button
                                                    key={action}
                                                    id={buildId(
                                                      'security-manager-tree-add-field-action',
                                                      `${entry.root}-${resource.resource}-${field.field}-${action}`
                                                    )}
                                                    onClick={() =>
                                                      handleAddFieldPolicy(
                                                        entry.root,
                                                        resource.resource,
                                                        field.field,
                                                        action
                                                      )
                                                    }
                                                    disabled={!canEditGroup || already}
                                                    className="rounded px-2 py-1 text-xs font-semibold text-blue-700 hover:text-blue-800 disabled:opacity-30"
                                                    aria-label={`Add ${action} ${entry.root}:${resource.resource}:${field.field}`}
                                                  >
                                                    <span className="underline decoration-dotted">
                                                      {action}
                                                    </span>
                                                  </button>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          </aside>
        </div>
      )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
